import { useCallback, useEffect, useState } from 'react';

type ThemeChoice = 'light' | 'dark';
type Effective = 'light' | 'dark';

const STORAGE_KEY = 'thirukkural-theme';
const DEFAULT_THEME: Effective = 'dark';

function readSavedChoice(): ThemeChoice {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return DEFAULT_THEME;
}

function applyEffective(effective: Effective) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', effective);
}

/**
 * Theme state hook.
 *
 * Dark is the default — the app opens dark on a fresh visit regardless
 * of the operating system's colour-scheme preference. Users can toggle
 * to light; their choice persists in localStorage.
 *
 * localStorage-scoped (not URL-scoped like ``useLang``) because theme
 * is a personal preference, not a shareable one — a kural link
 * shouldn't force the recipient into a mode.
 */
export function useTheme(): {
  choice: ThemeChoice;
  effective: Effective;
  toggle: () => void;
  setChoice: (choice: ThemeChoice) => void;
} {
  const [choice, setChoiceState] = useState<ThemeChoice>(readSavedChoice);

  useEffect(() => {
    applyEffective(choice);
  }, [choice]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    setChoice(choice === 'dark' ? 'light' : 'dark');
  }, [choice, setChoice]);

  return { choice, effective: choice, toggle, setChoice };
}

